import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileCheck2, Loader2, Play, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const STYLE = { PASS: "text-emerald-300 border-emerald-500/40", FAIL: "text-red-300 border-red-500/40", NOT_CONFIGURED: "text-amber-300 border-amber-500/40", BLOCKED: "text-orange-300 border-orange-500/40", SKIPPED: "text-slate-400 border-white/15" };

export function EvidencePanel() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [log, setLog] = useState("");

  const load = () => api.get("/admin/evidence").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!data?.running) return;
    const t = setInterval(() => { load(); api.get("/admin/evidence/log").then((r) => setLog(r.data.log)).catch(() => {}); }, 4000);
    return () => clearInterval(t);
  }, [data?.running]);

  const run = async (full) => {
    try { await api.post(`/admin/evidence/run?full=${full}`); toast.message(full ? "Full audit started (renders a real video)" : "Release gate started"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start audit"); }
  };
  const m = data?.latest;
  const tests = (m?.tests || []).filter((t) => !filter || t.status === filter);

  return (
    <section data-testid="evidence-panel" className="glass rounded-2xl p-5 mt-8">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex items-center gap-2 text-sm font-semibold"><FileCheck2 size={16} className="text-purple-300" /> Evidence Center · deterministic release gate (T01–T75)</div>
        <div className="flex-1" />
        <button data-testid="evidence-refresh" onClick={load} className="p-1.5 rounded-md hover:bg-white/10 text-slate-400"><RefreshCw size={14} /></button>
        <button data-testid="evidence-run-btn" onClick={() => run(false)} disabled={data?.running} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-purple-600 text-white disabled:opacity-50">{data?.running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Run gate</button>
        <button data-testid="evidence-run-full-btn" onClick={() => run(true)} disabled={data?.running} className="text-xs px-3 py-1.5 rounded-full border border-white/15 disabled:opacity-50">Run full (+video render)</button>
      </div>
      <p className="text-xs text-slate-500 mb-4">Only this test code awards PASS — it boots, calls real APIs, decodes real media with ffmpeg, restarts the backend and checks RBAC with live 401/403 responses. Optional providers without credentials are NOT_CONFIGURED, never PASS.</p>
      {data?.running && <pre data-testid="evidence-live-log" className="text-[11px] font-mono bg-black/40 rounded-lg p-3 max-h-40 overflow-y-auto text-slate-400 mb-4">{log || "starting…"}</pre>}
      {!m ? (
        <div data-testid="evidence-empty" className="text-sm text-slate-500">No manifest yet — run the gate.</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span data-testid="evidence-gate" className={`text-xs font-mono px-3 py-1 rounded-full border ${m.release_gate === "PASS" ? STYLE.PASS : STYLE.FAIL}`}>GATE {m.release_gate}</span>
            {Object.entries(m.counts).map(([k, v]) => (
              <button key={k} data-testid={`evidence-count-${k}`} onClick={() => setFilter(filter === k ? "" : k)} className={`text-[11px] font-mono px-2.5 py-1 rounded-full border ${STYLE[k]} ${filter === k ? "bg-white/10" : ""}`}>{k} {v}</button>
            ))}
            <span className="text-[11px] text-slate-500 font-mono ml-auto">build {m.build_hash} · {new Date(m.generated_at).toLocaleString()} · {m.duration_s}s{m.full ? " · full" : ""}</span>
          </div>
          {m.credentials_required?.length > 0 && <div className="text-xs text-amber-300 mb-3">Credentials still required: {m.credentials_required.join(", ")}</div>}
          {m.not_implemented?.length > 0 && <div className="text-xs text-red-300 mb-3">Genuinely unresolved (NOT_IMPLEMENTED): {m.not_implemented.join(" · ")}</div>}
          <div className="max-h-[420px] overflow-y-auto rounded-lg border border-white/10">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#0A0C14]"><tr className="text-left text-slate-500 font-mono"><th className="p-2">ID</th><th className="p-2">Capability</th><th className="p-2">Status</th><th className="p-2">Actual execution</th></tr></thead>
              <tbody>
                {tests.map((t) => (
                  <tr key={t.test_id} data-testid={`evidence-row-${t.test_id}`} className="border-t border-white/5 align-top">
                    <td className="p-2 font-mono text-slate-400">{t.test_id}</td>
                    <td className="p-2 text-slate-200">{t.capability}{!t.required && <span className="ml-1 text-[10px] text-slate-500">optional</span>}</td>
                    <td className="p-2"><span className={`font-mono px-2 py-0.5 rounded-full border ${STYLE[t.status]}`}>{t.status}</span></td>
                    <td className="p-2 text-slate-400 break-all">{t.actual_execution}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
